import React, { useState } from "react";
import { Button, ButtonVariants, Facilities, Facility } from "@app/shared";
import { useFetch } from "../../../../../hooks";
import { Icons } from "../../../../shared";

interface LocationFacilitiesStepProps {
    selectedGroupId: string;
    groupName: string;
    onNext: (selectedFacilityId?: string, selectedFacilityName?: string, newFacilityData?: Partial<Facility>) => void;
    onBack: () => void;
}

export const LocationFacilitiesStep: React.FC<LocationFacilitiesStepProps> = ({ selectedGroupId, groupName, onNext, onBack }) => {
    const [facilities, , fetchHelpers] = useFetch(() => Facilities.getByGroupId(selectedGroupId), [selectedGroupId]);
    const [selectedFacilityId, setSelectedFacilityId] = useState<string | undefined>(undefined);
    const [showCreateNew, setShowCreateNew] = useState(false);

    const activeFacilities = facilities?.filter((facility) => !facility.isDeleted) || [];
    const hasExistingFacilities = activeFacilities.length > 0;
    const handleSelectFacility = (facilityId: string | undefined) => {
        setSelectedFacilityId(facilityId);
        setShowCreateNew(false);
    };

    const handleCreateNewClick = () => {
        setShowCreateNew(true);
        setSelectedFacilityId(undefined);
    };

    const handleContinueWithExisting = () => {
        if (selectedFacilityId) {
            const facility = facilities?.find((s) => s.id === selectedFacilityId);
            onNext(selectedFacilityId, facility?.name);
        }
    };

    const handleCreateNewFacility = () => {
        onNext(undefined, undefined, { facilityGroupId: selectedGroupId });
    };

    if (fetchHelpers.isLoading) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
                    <p className="text-neutral-600">Loading locations...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto">
            <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icons.LocationOutline className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-2">Choose Location</h2>
                <p className="text-neutral-600 mb-4">{hasExistingFacilities ? "Select an existing location or create a new one for your device" : "No locations found in this group. Let's create your first location!"}</p>
                <div className="inline-flex items-center px-4 py-2 bg-white border border-warmGray-200 rounded-full text-sm shadow-sm">
                    <div className="w-2 h-2 bg-brand-primary rounded-full mr-2"></div>
                    <span className="text-neutral-700 font-medium">Group: {groupName}</span>
                </div>
            </div>

            {hasExistingFacilities && (
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4">Existing Locations</h3>
                    <div className="grid gap-3">
                        {activeFacilities.map((facility) => (
                            <div key={facility.id} onClick={() => handleSelectFacility(facility.id)} className={`group p-5 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${selectedFacilityId === facility.id ? "border-brand-primary bg-terracotta-50 shadow-md ring-1 ring-brand-primary ring-opacity-30 shadow-brand-glow" : "border-warmGray-200 hover:border-warmGray-300 hover:bg-warmGray-50"}`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 transition-colors ${selectedFacilityId === facility.id ? "bg-brand-primary" : "bg-warmGray-100 group-hover:bg-warmGray-200"}`}>
                                            <Icons.LocationOutline className={`w-6 h-6 ${selectedFacilityId === facility.id ? "text-white" : "text-warmGray-600"}`} />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-neutral-900 mb-1">{facility.name}</h4>
                                            <div className="text-sm text-neutral-500 space-y-1">
                                                {facility.locationCode && <p>Code: {facility.locationCode}</p>}
                                                {facility.address1 && (
                                                    <p>
                                                        {facility.address1}
                                                        {facility.city ? `, ${facility.city}` : ""}
                                                    </p>
                                                )}
                                                <p>
                                                    {facility.deviceCount || 0} {facility.deviceCount === 1 ? "device" : "devices"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        {selectedFacilityId === facility.id && (
                                            <div className="w-5 h-5 bg-brand-primary rounded-full flex items-center justify-center mr-3">
                                                <div className="w-2 h-2 bg-white rounded-full"></div>
                                            </div>
                                        )}
                                        <Icons.ChevronRight className="w-5 h-5 text-warmGray-400 group-hover:text-warmGray-600 transition-colors" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {selectedFacilityId && (
                        <div className="mt-4">
                            <Button onClick={handleContinueWithExisting} className="w-full bg-brand-primary hover:bg-brand-primary-darkened text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl">
                                Continue with Selected Location
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <div className="mb-8">
                {hasExistingFacilities && (
                    <div className="flex items-center mb-6">
                        <div className="flex-1 border-t border-warmGray-200"></div>
                        <span className="px-4 text-warmGray-500 text-sm font-medium">OR</span>
                        <div className="flex-1 border-t border-warmGray-200"></div>
                    </div>
                )}

                <button onClick={handleCreateNewClick} className={`flex justify-center items-center w-full h-20 p-6 border-2 border-dashed rounded-xl transition-all group ${showCreateNew ? "border-brand-primary bg-terracotta-50" : "border-warmGray-300 hover:border-brand-primary hover:bg-terracotta-50"}`}>
                    <div className="flex items-center justify-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-colors ${showCreateNew ? "bg-brand-primary" : "bg-warmGray-100 group-hover:bg-brand-primary"}`}>
                            <Icons.AddOutline className={`w-5 h-5 transition-colors ${showCreateNew ? "text-white" : "text-warmGray-600 group-hover:text-white"}`} />
                        </div>
                        <span className="text-neutral-700 font-semibold">Create New Location</span>
                    </div>
                </button>

                {showCreateNew && (
                    <div className="mt-4 p-6 bg-gradient-to-r from-terracotta-50 to-warmGray-50 border border-brand-primary border-opacity-30 rounded-xl">
                        <div className="text-center space-y-4">
                            <div>
                                <h4 className="font-semibold text-neutral-900 mb-3">Ready to Create New Location</h4>
                                <p className="text-sm text-neutral-600 leading-relaxed">You'll be able to enter the location details including name, address, and coordinates on the next step.</p>
                            </div>

                            <div className="flex gap-3">
                                <Button onClick={handleCreateNewFacility} className="flex-1 bg-brand-primary hover:bg-brand-primary-darkened text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl">
                                    Continue to Location Details
                                </Button>
                                <Button variant={ButtonVariants.Secondary} onClick={() => setShowCreateNew(false)} className="flex-1 bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 font-semibold rounded-xl transition-all duration-200 hover:bg-warmGray-50">
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-gradient-to-r from-warmGray-50 to-white border border-warmGray-100 rounded-xl p-6">
                <div className="flex items-start">
                    <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <Icons.LocationOutline className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-neutral-900 mb-2">What is a Location?</h4>
                        <p className="text-sm text-neutral-600 leading-relaxed">A location is a specific place within your location group where devices are installed. For example, within "Main Campus" you might have "Parking Entry", "Main Entrance", or "Parking Exit" as individual locations.</p>
                    </div>
                </div>
            </div>

            <div className="mt-6 text-center">
                <button onClick={onBack} className="inline-flex items-center text-warmGray-500 hover:text-brand-primary text-sm font-medium transition-colors">
                    <Icons.ChevronRight className="w-4 h-4 mr-1 rotate-180" />
                    Back to Location Groups
                </button>
            </div>
        </div>
    );
};
